import create, { UseStore } from 'zustand'
import { userProfileApi } from '../api/user-profile-api'
import { AysncFn, fetchRequest, organizationApi } from './'
import { Subscription, subscription } from './api'
import { AuditDomain, AuditResult, resultProps } from './audit-report'
import { Worksheet } from './domain'
import { showToast } from './toast'
import { VisitSortField } from './visit-queue'
import { WorksheetResult } from './worksheet-service'

const service = 'auditscorecard'
const serviceAudtQuery = 'myaudit-query'

const url = () => `/${service}/process`

function requestBody<T = any>(action?: string, data?: T) {
  return {
    service,
    action,
    data: { ...data },
  }
}

type AuditArgs = {
  auditId: string
  pageNumber?: number
  pageSize?: number
  sort?: VisitSortField[]
}

export type BubbleChartData = {
  category: string
  totalPoints: number
  totalPossiblePoints: number
  percentage: number
}
type NumberMap = {
  [key: string]: number
}
export type BarChartData = {
  [key: string]: NumberMap
}
type ChangeReasons = {
  [key: string]: string
}

export type AuditReportSummary = {
  auditId: string
  bubbleChartData: BubbleChartData[]
  barChartData: BarChartData
  changeReasonsDescriptions: ChangeReasons
}
type SummaryResult = WorksheetResult<AuditReportSummary>

function summaryDataRequest() {
  return fetchRequest<AuditArgs, SummaryResult>({
    method: 'post',
    url,
    body: (a: AuditArgs) => requestBody('getsummarydata', a),
    notFoundResult: () => ({ result: [] }),
  })
}
export const getAuditSummary: AysncFn<AuditArgs, SummaryResult> = summaryDataRequest()

export type AuditDetailArgs = AuditArgs & {
  bubbleCategory?: string
  changeReason?: string
}

export type AuditWorksheet = {
  visitId: string
  worksheetId: string
  locationCode: string
  departmentCode: string
  accountNumber: string
  auditId: string
  serviceDate: string
  patientName: string
  coderName: string
  auditorName: string
  physicianName: string
  totalPossiblePoints: number
  totalActualPoints: number
  totalCPTPoints: number
  totalICDPoints: number
  hasChanges: number
}

export type AuditDetails = {
  pageNumber: number
  pageSize: number
  totalRecords: number
  worksheets: Worksheet[]
}

type AuditDetailResult = WorksheetResult<AuditDetails>

function detailRequest() {
  return fetchRequest<AuditDetailArgs, AuditDetailResult>({
    method: 'post',
    url,
    body: (a: AuditDetailArgs) => requestBody('getdetaildata', a),
    notFoundResult: () => ({ result: [] }),
  })
}

export const getAuditDetails: AysncFn<AuditDetailArgs, AuditDetailResult> = detailRequest()

export function auditSummaryHasResults(a: AuditReportSummary) {
  if (!a) {
    return false
  }
  const { bubbleChartData } = a

  if (!bubbleChartData?.length) {
    return false
  }

  if (bubbleChartData.length === 1) {
    if (bubbleChartData[0].totalPossiblePoints === 0) {
      return false
    }
  }

  return true
}

export type AuditQuery = {
  locationCode: string | string[]
  departmentCode: string | string[]
  worksheetType?: string | string[]
  auditId?: string | string[]
  providerId?: string | string[]
  auditedId?: string | string[]
  startDate: string | Date
  endDate: string | Date
}

type AuditQueryData = {
  audited: UseStore<AuditQuery>
  auditor: UseStore<AuditQuery>
}

export const auditQueryData = create<AuditQueryData>((s) => ({
  audited: null,
  auditor: null,
}))

export const getDateRange: AysncFn<any, any> = dateRangeRequest()

function dateRangeRequest() {
  return fetchRequest<any, any>({
    method: 'get',
    url: () => `/${serviceAudtQuery}/datepickerconfig`,
    notFoundResult: () => ({ result: [] }),
  })
}

const getAuditedResultsURL = fetchRequest<AuditQuery, AuditResult>({
  method: 'post',
  url: () => `/${serviceAudtQuery}/auditedsummary/userid/${userProfileApi.data.getState().profile.principalId}`,
  body: (query: AuditQuery) => query,
  notFoundResult: () => ({ result: [] }),
})

const getAuditedScatterDataURL = fetchRequest<AuditQuery, AuditResult>({
  method: 'post',
  url: () => `/${serviceAudtQuery}/auditedscatterchart/userid/${userProfileApi.data.getState().profile.principalId}`,
  body: (query: AuditQuery) => query,
  notFoundResult: () => ({ result: [] }),
})

const getAuditorResultsURL = fetchRequest<AuditQuery, AuditResult>({
  method: 'post',
  url: () => `/${serviceAudtQuery}/auditorsummary/userid/${userProfileApi.data.getState().profile.principalId}`,
  body: (query: AuditQuery) => query,
  notFoundResult: () => ({ result: [] }),
})

const getAuditorScatterDataURL = fetchRequest<AuditQuery, AuditResult>({
  method: 'post',
  url: () => `/${serviceAudtQuery}/auditorscatterchart/userid/${userProfileApi.data.getState().profile.principalId}`,
  body: (query: AuditQuery) => query,
  notFoundResult: () => ({ result: [] }),
})

export const getAuditedWorksheetDetails = fetchRequest<any, any>({
  method: 'post',
  url: () => `/${serviceAudtQuery}/auditeddetail/userid/${userProfileApi.data.getState().profile.principalId}`,
  body: (query: any) => {
    query?.filter?.fields.map((i) => i?.sortOrder && delete i.sortOrder)
    return query
  },
  notFoundResult: () => ({ result: [] }),
})

export const getAuditorWorksheetDetails = fetchRequest<any, any>({
  method: 'post',
  url: () => `/${serviceAudtQuery}/auditordetail/userid/${userProfileApi.data.getState().profile.principalId}`, //${ userProfileApi.data.getState().profile.principalId}`
  body: (query: any) => {
    query?.filter?.fields.map((i) => i?.sortOrder && delete i.sortOrder)
    return query
  },
  notFoundResult: () => ({ result: [] }),
})

type AuditReportActions = {
  refreshAuditReport?: (query?: AuditQuery) => void
  getAuditedResults?: (query?: AuditQuery) => void
  getAuditorResults?: (query?: AuditQuery) => void
  requestPage?: (a: { pageNumber: number; pageSize: number }) => void
}

export type AuditReportApi = {
  data: UseStore<AuditReport>
  actions: AuditReportActions
  events: Subscription<AuditReport>
}

type ScatterProps = {
  accountNumber: string
  audited: {
    userId: string
    workflow: string
    lastName: string
    firstName: string
    email: string
  }
  departmentCode: string | string[]
  filter: { predicate: 'AND'; fields: any }
  locationCode: string | string[]
  mrn: string
  percentage: number
  serviceDate: number | string | Date
  totalPoints: number
  totalPossiblePoints: number
  worksheetId: string
  worksheetType: string | string[]
}

type AuditReport = {
  locationCodes: string | string[]
  departmentCodes: string | string[]
  worksheetTypes: string | string[]
  startDate: string | Date
  endDate: string | Date
  audited: {
    domain: AuditDomain
    result: resultProps
    scatter: ScatterProps[]
    auditId?: string | string[]
    providerId?: string | string[]
    reload?: boolean
    clear?: boolean
  }
  auditor: {
    domain: AuditDomain
    result: resultProps
    scatter: ScatterProps[]
    auditId?: string | string[]
    auditedId?: string | string[]
    providerId?: string | string[]
    reload?: boolean
    clear?: boolean
  }
  worksheetReload?: boolean
  reload?: boolean
}

function auditReportApi(): AuditReportApi {
  const data = create<AuditReport>((s) => ({
    locationCodes: null,
    departmentCodes: null,
    worksheetTypes: null,
    startDate: null,
    endDate: null,
    audited: {
      domain: null,
      result: null,
      scatter: null,
      auditId: null,
      providerId: null,
      reload: false,
      clear: false,
    },
    auditor: {
      domain: null,
      result: null,
      scatter: null,
      auditId: null,
      providerId: null,
      audited: null,
      reload: false,
      clear: false,
    },
    worksheetReload: false,
    reload: false,
  }))

  async function searchAudited(a: AuditQuery) {
    if (a?.locationCode) {
      const args = {
        locationCode: organizationApi.getState().selection.location.code,
        departmentCode: organizationApi.getState().selection.department.code || 'URGENTCARE',
        worksheetType: a.worksheetType,
        auditId: a.auditId,
        providerId: a.providerId,
        startDate: a.startDate,
        endDate: a.endDate,
      }

      const response = await getAuditedResultsURL(args)

      const responseScater = await getAuditedScatterDataURL(args)

      if (response.success || responseScater.success) {
        let tempDomain = response.domain

        if (args.auditId && args.auditId.length > 0) {
          tempDomain['Audit Batches'] = data.getState().audited.domain['Audit Batches']
        }
        if (args.providerId && args.providerId.length > 0) {
          tempDomain['Audit Batches'] = data.getState().audited.domain['Audit Batches']
          tempDomain.Providers = data.getState().audited.domain.Providers
        }
        data.setState({
          audited: {
            domain: tempDomain,
            result: response.result,
            scatter: responseScater.result as any,
            auditId: args?.auditId,
            providerId: args.providerId,
            reload: data.getState().audited.reload,
            clear: data.getState().audited.clear,
          },
          locationCodes: response.locationCodes,
          departmentCodes: response.departmentCodes,
          worksheetTypes: response.worksheetTypes,
          startDate: response.startDate,
          endDate: response.endDate,
          worksheetReload: data.getState().worksheetReload,
        })
        data.setState({ reload: false })
      } else {
        const message = response.error || response.message
        console.log('audit query error', message)
        if (response?.error === 'Unable to find aggregated worksheet audits') {
          data.setState({
            locationCodes: response.locationCodes,
            departmentCodes: response.departmentCodes,
            worksheetTypes: response.worksheetTypes,
            startDate: response.startDate,
            endDate: response.endDate,
            audited: {
              domain: null,
              result: null,
              scatter: null,
              auditId: null,
              providerId: null,
              reload: false,
              clear: false,
            },
            reload: false,
          })
        } else {
          showToast(message)
        }
      }
    }
  }

  async function searchAuditor(a: AuditQuery) {
    if (a?.locationCode) {
      const args = {
        locationCode: organizationApi.getState().selection.location.code,
        departmentCode: organizationApi.getState().selection.department.code || 'URGENTCARE',
        worksheetType: a.worksheetType,
        auditId: a.auditId,
        providerId: a.providerId,
        auditedId: a.auditedId,
        startDate: a.startDate,
        endDate: a.endDate,
      }

      const response = await getAuditorResultsURL(args)

      const responseScater = await getAuditorScatterDataURL(args)

      if (response.success || responseScater.success) {
        let tempDomain = response.domain

        if (args.auditId && args.auditId.length > 0) {
          tempDomain['Audit Batches'] = data.getState().auditor.domain['Audit Batches']
        }
        if (args.providerId && args.providerId.length > 0) {
          tempDomain['Audit Batches'] = data.getState().auditor.domain['Audit Batches']
          tempDomain.Providers = data.getState().auditor.domain.Providers
        }

        if (args.auditedId && args.auditedId.length > 0) {
          tempDomain['Audit Batches'] = data.getState().auditor.domain['Audit Batches']
          tempDomain.Audited = data.getState().auditor.domain.Audited
        }
        data.setState({
          auditor: {
            domain: tempDomain,
            result: response.result,
            scatter: responseScater.result as any,
            auditId: args?.auditId,
            providerId: args.providerId,
            auditedId: args?.auditedId,
            reload: data.getState().auditor.reload,
            clear: data.getState().auditor.clear,
          },
          locationCodes: response.locationCodes,
          departmentCodes: response.departmentCodes,
          worksheetTypes: response.worksheetTypes,
          startDate: response.startDate,
          endDate: response.endDate,
          worksheetReload: data.getState().worksheetReload,
        })
      } else {
        const message = response.error || response.message
        console.log('audit query error', message)

        if (response?.error === 'Unable to find aggregated worksheet audits') {
          data.setState({
            locationCodes: response.locationCodes,
            departmentCodes: response.departmentCodes,
            worksheetTypes: response.worksheetTypes,
            startDate: response.startDate,
            endDate: response.endDate,
            auditor: {
              domain: null,
              result: null,
              scatter: null,
              auditId: null,
              providerId: null,
              reload: false,
              clear: false,
            },
          })
        } else {
          showToast(message)
        }
      }
    }
  }

  async function getAuditedResults(a?: AuditQuery) {
    const s = organizationApi.getState().selection
    const locationCode = s && s.location && s.location.code
    locationCode &&
      searchAudited({
        locationCode,
        departmentCode: organizationApi.getState().selection.department.code || 'URGENTCARE',
        worksheetType: a.worksheetType,
        auditId: a.auditId,
        providerId: a.providerId,
        startDate: a.startDate,
        endDate: a.endDate,
      })
  }

  async function getAuditorResults(a?: AuditQuery) {
    const s = organizationApi.getState().selection
    const locationCode = s && s.location && s.location.code
    locationCode &&
      searchAuditor({
        locationCode,
        departmentCode: organizationApi.getState().selection.department.code || 'URGENTCARE',
        worksheetType: a.worksheetType,
        auditId: a.auditId,
        providerId: a.providerId,
        auditedId: a.auditedId,
        startDate: a.startDate,
        endDate: a.endDate,
      })
  }

  const actions: AuditReportActions = {
    getAuditedResults,
    getAuditorResults,
  }

  const events = subscription<AuditReport>(['audited', 'auditor'], data)

  const api = { data, actions, events }

  return api
}

export const auditReportCall = auditReportApi()
